import { API_ROUTES } from '@shared/api-utils';
import type { Locale } from 'next-intl';
import type { LotteryName } from '../../lottery-purchases/types';
import type { LotteryConfig } from '../types';

type LotteryDrawing = LotteryConfig['drawings'][number];

/**
 * Builds the API path used to fetch lottery configuration for a given lottery.
 *
 * @param lotteryName - Technical lottery identifier (e.g. `'irishLotto'`).
 * @param locale - Locale to use for the API request.
 * @returns The lottery config API route with `:lotteryName` substituted.
 */
function getLotteryConfigPath(lotteryName: LotteryName, locale: Locale): string {
  return API_ROUTES.LOTTERIES.LOTTERY_CONFIG.replace(':lotteryName', lotteryName).replace(':locale', locale);
}

/**
 * Returns the next drawing that is still sellable: the earliest drawing whose
 * `closingDate` is strictly after `cutoffTime`.
 *
 * @param lotteryConfig - Lottery configuration containing the drawings list.
 * @param cutoffTime - Unix timestamp in milliseconds compared against each drawing's `closingDate`.
 * @returns The next sellable drawing, or `undefined` if none remain open.
 */
function getNextSellableDrawing(lotteryConfig: LotteryConfig, cutoffTime: number): LotteryDrawing | undefined {
  return lotteryConfig.drawings.reduce<LotteryDrawing | undefined>((nextDrawing, drawing) => {
    if (drawing.closingDate <= cutoffTime) {
      return nextDrawing;
    }

    if (!nextDrawing || drawing.closingDate < nextDrawing.closingDate) {
      return drawing;
    }

    return nextDrawing;
  }, undefined);
}

/**
 * Returns the most recently closed drawing: the drawing with the latest
 * `closingDate` that is at or before `cutoffTime`.
 *
 * Used after a countdown ends to identify which drawing just closed, so a
 * config refresh can be validated against it.
 *
 * @param lotteryConfig - Lottery configuration containing the drawings list.
 * @param cutoffTime - Unix timestamp in milliseconds compared against each drawing's `closingDate`.
 * @returns The most recently closed drawing, or `undefined` if none have closed.
 */
function getMostRecentlyClosedDrawing(lotteryConfig: LotteryConfig, cutoffTime: number): LotteryDrawing | undefined {
  return lotteryConfig.drawings.reduce<LotteryDrawing | undefined>((closedDrawing, drawing) => {
    if (drawing.closingDate > cutoffTime) {
      return closedDrawing;
    }

    if (!closedDrawing || drawing.closingDate > closedDrawing.closingDate) {
      return drawing;
    }

    return closedDrawing;
  }, undefined);
}

/**
 * Returns whether a drawing is no longer present in a lottery config's `drawings` list.
 *
 * Used after a closing-date refresh: config payloads usually include several upcoming
 * drawings, so the reliable signal that the backend has moved on is that the closed
 * drawing has dropped out of the list.
 *
 * @param drawing - Drawing expected to have been removed (typically the one that just closed).
 * @param lotteryConfig - Lottery configuration to inspect.
 * @returns `true` if `drawing.id` is absent from `lotteryConfig.drawings`.
 */
function isDrawingGoneFromConfig(drawing: LotteryDrawing, lotteryConfig: LotteryConfig): boolean {
  return !lotteryConfig.drawings.some(candidate => candidate.id === drawing.id);
}

type TippengineNextDraw = {
  date: number | undefined;
  showNextDraw: boolean;
};

type TippengineDrawState = {
  sellableDrawing: LotteryDrawing | undefined;
  nextDraw: TippengineNextDraw;
};

/**
 * Resolves tippengine draw presentation after close: jackpot follows the
 * next sellable drawing, and "Next draw" uses the following drawing's date.
 */
function getTippengineDrawState(lotteryConfig: LotteryConfig, cutoffTime: number): TippengineDrawState {
  const sellableDrawing = getNextSellableDrawing(lotteryConfig, cutoffTime);
  const closedDrawing = getMostRecentlyClosedDrawing(lotteryConfig, cutoffTime);
  const followingDrawing = sellableDrawing ? getNextSellableDrawing(lotteryConfig, sellableDrawing.closingDate + 1) : undefined;

  return {
    sellableDrawing,
    nextDraw: {
      date: followingDrawing?.drawingDate ?? sellableDrawing?.drawingDate,
      showNextDraw: Boolean(closedDrawing && Date.now() < closedDrawing.drawingDate),
    },
  };
}

export { getLotteryConfigPath, getMostRecentlyClosedDrawing, getNextSellableDrawing, getTippengineDrawState, isDrawingGoneFromConfig };
export type { LotteryDrawing, TippengineDrawState, TippengineNextDraw };
